import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchDashboardStatsAPI, getUserData } from "../../utils/api";
import { ErrorAlert, StatCard } from "../../components/ui";
import "./Dashboard.css";

const QUICK_ACTIONS = [
  {
    to: "/admin/test-series/create",
    icon: "bi bi-plus-lg",
    title: "New test series",
    text: "Set up a test and its sections",
  },
  {
    to: "/admin/courses",
    icon: "bi bi-mortarboard",
    title: "Courses & plans",
    text: "Manage courses and pricing",
  },
  {
    to: "/admin/users",
    icon: "bi bi-people",
    title: "Students",
    text: "Browse registered users",
  },
  {
    to: "/admin/results/leaderboard",
    icon: "bi bi-trophy",
    title: "Leaderboards",
    text: "See how students are doing",
  },
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

function Dashboard() {
  const [stats, setStats] = useState({
    registered_users: 0,
    test_series_count: 0,
    plan_count: 0,
    courses: [],
    active_subscription: 0,
    recent_attempts: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userData = getUserData();
  const firstName = (userData?.name || "there").split(" ")[0];

  const loadDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchDashboardStatsAPI();
      if (response?.success) {
        setStats((prev) => ({
          ...prev,
          ...response.data,
          courses: response.data?.courses || [],
          recent_attempts: response.data?.recent_attempts || [],
        }));
      } else if (response) {
        setError(response.message || "We couldn't load dashboard statistics.");
      }
    } catch (err) {
      console.error("Error loading dashboard stats:", err);
      setError("We couldn't reach the server to load dashboard statistics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  const statCards = [
    { icon: "bi bi-journal-text", tone: "brand", value: stats.test_series_count, label: "Test series" },
    { icon: "bi bi-people", tone: "info", value: stats.registered_users, label: "Registered users" },
    {
      icon: "bi bi-patch-check",
      tone: "success",
      value: stats.active_subscription,
      label: "Active subscribers",
    },
    { icon: "bi bi-card-list", tone: "warning", value: stats.plan_count, label: "Subscription plans" },
  ];

  // Activity; each shows only when the server sends it
  const activityCards = [
    {
      icon: "bi bi-currency-rupee",
      tone: "success",
      value: stats.revenue_this_month,
      format: (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
      label: "Revenue this month",
      hint: "Paid online",
    },
    { icon: "bi bi-person-plus", tone: "info", value: stats.new_users_this_week, label: "New students", hint: "Last 7 days" },
    { icon: "bi bi-pencil-square", tone: "brand", value: stats.attempts_this_week, label: "Tests taken", hint: "Last 7 days" },
    { icon: "bi bi-hourglass-split", tone: "warning", value: stats.expiring_in_7_days, label: "Plans expiring", hint: "Next 7 days" },
  ].filter((card) => card.value !== undefined);

  return (
    <div className="dashboard page">
      <section className="dash-hero">
        <div className="dash-hero-text">
          <span className="dash-hero-eyebrow">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
          </span>
          <h2>
            {getGreeting()}, {firstName}
          </h2>
          <p>Here's a quick look at Jupiter Institute today.</p>
        </div>
        <Link to="/admin/test-series/create" className="btn-primary dash-hero-cta">
          <i className="bi bi-plus-lg"></i> Create test series
        </Link>
        <div className="dash-hero-orbit" aria-hidden="true"></div>
      </section>

      <ErrorAlert message={error} onRetry={loadDashboardStats} />

      <div className="stats-grid">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div className="stat-card" key={i}>
                <div className="skeleton" style={{ width: 48, height: 48, borderRadius: 14 }}></div>
                <div style={{ flex: 1 }}>
                  <div className="skeleton" style={{ width: "40%", height: 22, marginBottom: 8 }}></div>
                  <div className="skeleton" style={{ width: "70%", height: 12 }}></div>
                </div>
              </div>
            ))
          : statCards.map((card) => (
              <StatCard
                key={card.label}
                icon={card.icon}
                tone={card.tone}
                value={Number(card.value || 0).toLocaleString("en-IN")}
                label={card.label}
              />
            ))}
      </div>

      {!loading && activityCards.length > 0 && (
        <div className="stats-grid">
          {activityCards.map((card) => (
            <StatCard
              key={card.label}
              icon={card.icon}
              tone={card.tone}
              value={card.format ? card.format(card.value) : Number(card.value || 0).toLocaleString("en-IN")}
              label={card.label}
              hint={card.hint}
            />
          ))}
        </div>
      )}

      <div className="dash-grid">
        <section className="card dash-courses">
          <div className="card-header">
            <div>
              <h3>Courses</h3>
              <p className="text-muted dash-card-sub">
                {loading ? "Loading…" : `${stats.courses.length} available`}
              </p>
            </div>
            <Link to="/admin/courses" className="btn-ghost btn-sm">
              Manage <i className="bi bi-arrow-right"></i>
            </Link>
          </div>

          <div className="dash-course-list">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div className="dash-course-item" key={i}>
                  <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 12 }}></div>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton" style={{ width: "50%", height: 14, marginBottom: 6 }}></div>
                    <div className="skeleton" style={{ width: "30%", height: 10 }}></div>
                  </div>
                </div>
              ))
            ) : stats.courses.length > 0 ? (
              stats.courses.map((course) => (
                <div key={course.id} className="dash-course-item">
                  <span className="dash-course-icon">
                    {(course.course_name || "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="cell-stack">
                    <strong>{course.course_name}</strong>
                    <small>Created {formatDate(course.created_at)}</small>
                  </div>
                  <span className="badge badge-neutral">#{course.id}</span>
                </div>
              ))
            ) : (
              <div className="dash-empty">
                <i className="bi bi-mortarboard"></i>
                <p>No courses yet</p>
                <Link to="/admin/courses" className="btn-soft btn-sm">
                  Add a course
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="card dash-actions">
          <div className="card-header">
            <h3>Quick actions</h3>
          </div>
          <div className="dash-action-list">
            {QUICK_ACTIONS.map((action) => (
              <Link key={action.to} to={action.to} className="dash-action">
                <span className="dash-action-icon">
                  <i className={action.icon}></i>
                </span>
                <span className="cell-stack">
                  <strong>{action.title}</strong>
                  <small>{action.text}</small>
                </span>
                <i className="bi bi-chevron-right dash-action-arrow"></i>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {!loading && stats.recent_attempts.length > 0 && (
        <section className="card dash-recent">
          <div className="card-header">
            <div>
              <h3>Latest submitted tests</h3>
              <p className="text-muted dash-card-sub">The last {stats.recent_attempts.length} attempts</p>
            </div>
            <Link to="/admin/results/leaderboard" className="btn-ghost btn-sm">
              Leaderboards <i className="bi bi-arrow-right"></i>
            </Link>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Test</th>
                  <th>Score</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_attempts.map((a) => (
                  <tr key={a.attempt_id}>
                    <td>
                      <strong>{a.user_name || "—"}</strong>
                    </td>
                    <td>{a.test_series_title || "—"}</td>
                    <td>
                      <strong className="text-strong">{Number(a.score || 0)}</strong>
                      {a.max_marks != null && <span className="text-muted"> / {Number(a.max_marks)}</span>}
                    </td>
                    <td className="nowrap">
                      {a.submitted_at
                        ? new Date(a.submitted_at).toLocaleString("en-IN", {
                            day: "numeric",
                            month: "short",
                            hour: "numeric",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default Dashboard;
